import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Fragment } from 'react/jsx-runtime';

import { type InstanceDetailDataType } from '@/api/organization';
import Delete from '@/assets/svg/trashcan.svg?react';
import { Button } from '@/components/buttons';
import { LogicButton } from '@/components/buttons/LogicButton';
import { useTypedAppFormContext } from '@/components/Form';
import { Label } from '@/components/Form/fields';
import { Panel } from '@/components/panels';
import { Text } from '@/components/typography';

type Props = {
  isEditMode: boolean;
};

const CompanyVisitExclusions = ({ isEditMode }: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as InstanceDetailDataType });

  return (
    <Panel
      title={t`Visit Exclusions (Optional)`}
      infoPopover={{
        title: t`Visit Exclusions`,
        body: (
          <Trans>
            Visit exclusions are used to remove visits from reporting if the visitor views specific
            URLs. This will reduce the number of total visits reported within the platform. Any URL
            containing your input will match. To add another URL exclusion click the Add button
            while editing.
          </Trans>
        ),
      }}
    >
      <form.AppField name="visitExclusions" mode="array">
        {(arrayField) => (
          <div className="flex flex-col gap-2">
            <Label>
              <Trans>URL Exclusions</Trans>
            </Label>
            <div className="flex flex-col items-start gap-2">
              {arrayField.state.value.length === 0 && (
                <Text variant="body1" color="grey" italic weight="extra-light">
                  <Trans>no current URL visit exclusions</Trans>
                </Text>
              )}
              {arrayField.state.value.map((_, index) => (
                <Fragment key={`visit-exclusion-${index}`}>
                  {index !== 0 && <LogicButton value="OR" isReadOnly />}
                  <div className="flex w-full items-start gap-2">
                    <form.AppField name={`visitExclusions[${index}]`}>
                      {(field) => (
                        <field.Field className="w-full">
                          {isEditMode ? (
                            <field.TextInput
                              id={`visit-exclusion-${index}`}
                              placeholder={t`Type URL Path`}
                              size="medium"
                            />
                          ) : (
                            <Text variant="body1">{field.state.value}</Text>
                          )}
                          <field.Error />
                        </field.Field>
                      )}
                    </form.AppField>
                    {isEditMode && (
                      <form.Subscribe selector={(state) => ({ isSubmitting: state.isSubmitting })}>
                        {({ isSubmitting }) => (
                          <Button
                            variant="ghost"
                            shape="square"
                            danger
                            aria-label={t`Delete`}
                            disabled={isSubmitting}
                            size="icon"
                            onClick={() => arrayField.removeValue(index)}
                          >
                            <Delete />
                          </Button>
                        )}
                      </form.Subscribe>
                    )}
                  </div>
                </Fragment>
              ))}
              {isEditMode && (
                <form.Subscribe selector={(state) => ({ isSubmitting: state.isSubmitting })}>
                  {({ isSubmitting }) => (
                    <Button
                      className="mt-1"
                      variant="primary"
                      disabled={isSubmitting}
                      onClick={() => arrayField.pushValue('')}
                    >
                      <Trans>Add +</Trans>
                    </Button>
                  )}
                </form.Subscribe>
              )}
            </div>
          </div>
        )}
      </form.AppField>
    </Panel>
  );
};

export default CompanyVisitExclusions;
